import React from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Slider,
  Stack,
  SwipeableDrawer,
  Switch,
  Typography,
} from "@mui/material";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import _ from "lodash";
import { useGlobalSettings } from "../GlobalSettingsContext";
import { playNote } from "../audio/karplusStrong";
import { DEFAULT_VOLUME, MAX_VOLUME } from "../audio/volume";
import {
  FULL_NECK,
  HIGHEST_FRET,
  describeFretWindow,
  isFullNeck,
  windowFromThumbs,
} from "../fretWindow";
import { DEGREE_COLORS } from "../library/colors";
import { PITCH_CLASS_COUNT, formatNoteName } from "../library/notes";
import {
  OVERLAYS,
  OVERLAY_DEGREE_LABELS,
  OverlayKind,
  overlayRootSpelling,
} from "../library/overlays";

interface Props {
  open: boolean;
  onClose: () => void;
}

// Inlay frets, so the slider's ticks land where a guitarist's eye already goes.
const SLIDER_MARKS = [3, 5, 7, 9, 12, 15].map((value) => ({ value }));

// A tick where the app's original fixed loudness sits, so "normal" is easy to find again.
const VOLUME_MARKS = [{ value: DEFAULT_VOLUME }];

// The sheet covers the board, so the slider sounds this note itself when released — a
// mid-neck A, so the user can hear the new level against whatever else the phone is playing.
const PREVIEW_NOTE = { stringNum: 3, fretNum: 2 };

const OVERLAY_GROUPS = ["Roots", "Scales", "Chords"] as const;

/** Every control for the board, in one bottom sheet so the neck itself stays uncluttered. */
const FretboardSettingsSheet: React.FC<Props> = ({ open, onClose }) => {
  const { settings, updateSettings } = useGlobalSettings();
  const { overlayKind, overlayRootPitchClass, fretWindow, letRing, volume } = settings;
  const degreeLabels = overlayKind === "none" ? {} : OVERLAY_DEGREE_LABELS[overlayKind];
  const legendDegrees = Object.keys(degreeLabels)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          px: 2.5,
          pt: 1,
          pb: "calc(20px + env(safe-area-inset-bottom))",
          bgcolor: "background.paper",
        },
      }}
    >
      {/* Drag handle */}
      <Box
        sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: "divider", mx: "auto", mb: 2.5 }}
      />

      <Stack spacing={3}>
        <Stack spacing={1.5}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel id="overlay-label">Overlay</InputLabel>
              <Select
                labelId="overlay-label"
                label="Overlay"
                value={overlayKind}
                onChange={(e) => updateSettings({ overlayKind: e.target.value as OverlayKind })}
              >
                <MenuItem value="none">None</MenuItem>
                {OVERLAY_GROUPS.flatMap((group) => [
                  <ListSubheader key={group}>{group}</ListSubheader>,
                  ...Object.entries(OVERLAYS)
                    .filter(([, overlay]) => overlay.group === group)
                    .map(([kind, overlay]) => (
                      <MenuItem key={kind} value={kind}>
                        {_.upperFirst(overlay.label)}
                      </MenuItem>
                    )),
                ])}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 112 }} disabled={overlayKind === "none"}>
              <InputLabel id="key-label">Key</InputLabel>
              <Select
                labelId="key-label"
                label="Key"
                value={overlayRootPitchClass}
                onChange={(e) => updateSettings({ overlayRootPitchClass: Number(e.target.value) })}
              >
                {_.range(PITCH_CLASS_COUNT).map((pitchClass) => (
                  <MenuItem key={pitchClass} value={pitchClass}>
                    {formatNoteName(overlayRootSpelling(pitchClass, overlayKind))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Color legend mapping each tone's color to its degree */}
          {legendDegrees.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, px: 0.5 }}>
              {legendDegrees.map((degree) => (
                <Box key={degree} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      bgcolor: DEGREE_COLORS[degree],
                      border: "1.5px solid #fff",
                      boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
                    }}
                  />
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {degreeLabels[degree]}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Stack>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: 600 }}>{describeFretWindow(fretWindow)}</Typography>
            <Button
              size="small"
              disabled={isFullNeck(fretWindow)}
              onClick={() => updateSettings({ fretWindow: FULL_NECK })}
            >
              Show all
            </Button>
          </Box>
          <Slider
            aria-label="Frets shown"
            value={[fretWindow.startFret, fretWindow.endFret]}
            min={1}
            max={HIGHEST_FRET}
            step={1}
            marks={SLIDER_MARKS}
            valueLabelDisplay="auto"
            disableSwap
            onChange={(_e, value, activeThumb) => {
              const [startFret, endFret] = value as number[];
              updateSettings({
                fretWindow: windowFromThumbs(
                  startFret,
                  endFret,
                  activeThumb === 0 ? "start" : "end"
                ),
              });
            }}
            sx={{ mx: 1, width: "calc(100% - 16px)" }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 600 }}>Let notes ring</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Tapped notes sustain, so intervals and chords sound together
            </Typography>
          </Box>
          <Switch
            checked={letRing}
            onChange={(e) => updateSettings({ letRing: e.target.checked })}
          />
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 600 }}>Volume</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Balance the fretboard against a backing track playing in another app
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5 }}>
            <VolumeDownIcon sx={{ color: "text.secondary" }} />
            <Slider
              aria-label="Volume"
              value={volume}
              min={0}
              max={MAX_VOLUME}
              step={1}
              marks={VOLUME_MARKS}
              valueLabelDisplay="auto"
              onChange={(_e, value) => updateSettings({ volume: value as number })}
              onChangeCommitted={(_e, value) =>
                playNote(PREVIEW_NOTE.stringNum, PREVIEW_NOTE.fretNum, {
                  volume: value as number,
                })
              }
            />
            <VolumeUpIcon sx={{ color: "text.secondary" }} />
          </Box>
        </Box>
      </Stack>
    </SwipeableDrawer>
  );
};

export default FretboardSettingsSheet;
